package com.flowyride.core.map

import com.flowyride.core.domain.model.GeoPoint

/**
 * The ride as the navigation map draws it, so that a fix never encodes the whole track again: a
 * 1 000 km ride is ~85 000 points, several MB of GeoJSON to build, parse and cut into tiles every second.
 *
 * - [chunksGeoJson]: the whole track cut in chunks of [chunkSize] segments, built **once**. Each chunk
 *   carries its index ([CHUNK_PROPERTY]); the layers pick the done ones (`< chunk`) and the remaining
 *   ones (`> chunk`) with a filter, which costs nothing to change.
 * - [aroundGeoJson]: the few chunks around the rider, cut at the rider, the only data encoded at each fix.
 *
 * Both overlap by one chunk on each side: the two sources never update in the same frame, and a hole
 * in the line would flash right under the rider.
 */
internal class NavigationTrack(private val track: List<GeoPoint>, private val chunkSize: Int = CHUNK_SIZE) {

    init {
        require(chunkSize > 0) { "chunkSize must be positive" }
    }

    /** 0 when there is nothing to draw (less than two points). */
    val chunkCount: Int = if (track.size < 2) 0 else (track.size - 2) / chunkSize + 1

    val chunksGeoJson: String = GeoJson.featureCollection(
        List(chunkCount) { chunk ->
            GeoJson.lineString(points(firstIndex(chunk), lastIndex(chunk)), """{"$CHUNK_PROPERTY":$chunk}""")
        },
    )

    /** Chunk holding the segment that starts at [doneIndex]. */
    fun chunkAt(doneIndex: Int): Int =
        if (chunkCount == 0) 0 else (doneIndex.coerceIn(0, track.lastIndex) / chunkSize).coerceAtMost(chunkCount - 1)

    /**
     * The chunk of [doneIndex] and its two neighbours: what is done up to [doneIndex]
     * ([DONE_PROPERTY] `true`), then what remains.
     */
    fun aroundGeoJson(doneIndex: Int): String {
        if (chunkCount == 0) return GeoJson.featureCollection(emptyList())
        val chunk = chunkAt(doneIndex)
        val split = doneIndex.coerceIn(0, track.lastIndex)
        val from = firstIndex((chunk - 1).coerceAtLeast(0))
        val to = lastIndex((chunk + 1).coerceAtMost(chunkCount - 1))
        return GeoJson.featureCollection(
            listOfNotNull(
                if (split > from) GeoJson.lineString(points(from, split), """{"$DONE_PROPERTY":true}""") else null,
                if (split < to) GeoJson.lineString(points(split, to), """{"$DONE_PROPERTY":false}""") else null,
            ),
        )
    }

    private fun firstIndex(chunk: Int) = chunk * chunkSize

    /** Also the first point of the next chunk: no gap between two chunks. */
    private fun lastIndex(chunk: Int) = ((chunk + 1) * chunkSize).coerceAtMost(track.lastIndex)

    private fun points(from: Int, toInclusive: Int) = track.subList(from, toInclusive + 1)

    companion object {
        const val CHUNK_PROPERTY = "chunk"
        const val DONE_PROPERTY = "done"

        /** ~3 km of a routed track: about 15 kB to encode per fix, a filter to change every few minutes. */
        const val CHUNK_SIZE = 256
    }
}
